// One multiple-value autocomplete for the current owners. Chips are the owners;
// picking an option adds one and deleting a chip removes one. Replaces the
// separate "Add owner" popover.
import React from "react";
import { useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import { Button } from "components/haven-design/buttons";
import { OwnerSummary } from "./owners-editor";
import OwnerSelect, {
  AssignableOwnersData,
  OwnerOption,
  accessGrantPropertyName,
  buildInterestListNameMap,
  buildOptions,
  ownerManagesAll,
  ownersFromData,
  resolveCurrentPropertyName,
} from "./owner-select";
import AccessGrantWarning from "./access-grant-warning";
import { ownerLabel, ownerNameFrom } from "./owner-names";
import { ASSIGNABLE_OWNERS } from "./owner-queries";

type OwnerMultiEditorProps = {
  id: string;
  selectId: string;
  owners: OwnerSummary[];
  pendingUserIds?: ReadonlySet<string>;
  currentUserId: string;
  currentUserName: string;
  autoFocus?: boolean;

  /**
   * When set, picking an owner who doesn't manage this list asks to confirm the
   * access grant first (the server stays the source of truth for access).
   */
  interestListId?: string;

  /** Floats the property of the list the dashboard is filtered on to the top. */
  activeInterestListId?: string | null;

  onAdd: (userId: string, name: string | null) => void;
  onRemove: (userId: string) => void;
};

const chipOption = (owner: OwnerSummary): OwnerOption => ({
  userId: owner.id,
  label: ownerLabel(owner.name),
  group: "",
  isMe: false,
});

const OwnerMultiEditor: React.FC<OwnerMultiEditorProps> = ({
  id,
  selectId,
  owners,
  pendingUserIds,
  currentUserId,
  currentUserName,
  autoFocus = false,
  interestListId,
  activeInterestListId,
  onAdd,
  onRemove,
}) => {
  const { data, loading } = useQuery<AssignableOwnersData>(ASSIGNABLE_OWNERS, {
    fetchPolicy: "cache-and-network",
  });
  const [pendingGrant, setPendingGrant] = React.useState<OwnerOption | null>(null);

  const assignable = React.useMemo(() => ownersFromData(data), [data]);
  const profileName = React.useCallback((userId: string) => {
    const profile = assignable.find((owner) => owner.id === userId)?.profile;
    return profile ? ownerNameFrom(profile.firstName, profile.lastName) : null;
  }, [assignable]);

  const meName = React.useMemo(
    () => currentUserName.trim() || ownerLabel(profileName(currentUserId)),
    [currentUserName, currentUserId, profileName],
  );

  const options = React.useMemo(
    () => buildOptions(
      assignable,
      currentUserId,
      meName,
      resolveCurrentPropertyName(assignable, activeInterestListId),
    ),
    [assignable, currentUserId, meName, activeInterestListId],
  );

  const value = React.useMemo(() => owners.map(chipOption), [owners]);
  const busy = (pendingUserIds?.size ?? 0) > 0;

  const onChange = (next: OwnerOption[]) => {
    // A second pick must not replace an access grant that has not been confirmed.
    if (pendingGrant) return;

    const nextIds = new Set(next.map((option) => option.userId));
    const currentIds = new Set(owners.map((owner) => owner.id));

    owners.filter((owner) => !nextIds.has(owner.id)).forEach((owner) => onRemove(owner.id));

    const added = next.filter((option) => !currentIds.has(option.userId));
    const uniqueAdded = [...new Map(added.map((option) => [option.userId, option])).values()];
    for (const option of uniqueAdded) {
      const owner = assignable.find((candidate) => candidate.id === option.userId);
      if (interestListId && owner && !ownerManagesAll(owner, [interestListId])) {
        setPendingGrant(option);
        break;
      }
      onAdd(option.userId, profileName(option.userId));
    }
  };

  const confirmGrant = () => {
    if (!pendingGrant) return;
    onAdd(pendingGrant.userId, profileName(pendingGrant.userId));
    setPendingGrant(null);
  };

  return (
    <Box id={id} data-cy="owners-editor" sx={{ width: "100%", minWidth: 260 }}>
      <OwnerSelect
        multiple
        filterSelectedOptions
        listAboveModal
        required={false}
        size="small"
        id={selectId}
        options={options}
        value={value}
        loading={loading}
        disabled={busy || pendingGrant !== null}
        autoFocus={autoFocus}
        disabledUserIds={pendingUserIds}
        sx={{ width: "100%" }}
        chipDataCy={(option) => `owner-chip-${option.userId}`}
        deleteDataCy={(option) => `owner-remove-${option.userId}`}
        deleteAriaLabel={(option) => `Remove ${option.label} as owner`}
        onChange={onChange}
      />
      {pendingGrant && (
        <Box data-cy="owner-access-confirm">
          <AccessGrantWarning
            ownerName={pendingGrant.label}
            propertyName={accessGrantPropertyName(
              [interestListId ?? ""],
              buildInterestListNameMap(assignable),
            )}
            sx={{ mt: 1.5, mb: 1.5 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button color="secondary" size="S" onClick={() => setPendingGrant(null)} disabled={busy}>
              Cancel
            </Button>
            <Button color="primary" size="S" onClick={confirmGrant} disabled={busy}>
              {busy ? "Adding" : "Add anyway"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OwnerMultiEditor;
